"use client";

import { Box, Pagination } from "@repo/ui";
import ScheduleCard from "./ScheduleCard";
import NoScheduleScreen from "../common/NoScheduleScreen";
import StructuredData from "../common/StructuredData";
import { useSuspenseQuery } from "@apollo/client";
import { useParams, usePathname } from "next/navigation";
import { GET_EVENTS } from "@/graphql/queries/event-query";
import { useMemo, forwardRef, useImperativeHandle, useRef } from "react";
import { getCookie, hasCookie } from "cookies-next/client";

export type ScheduleWrapperRef = {
  refetch: (variables?: {
    date?: string;
    modeOfTraining?: string;
  }) => Promise<any>;
  wrapperDivRef: React.RefObject<HTMLDivElement | null>;
};

const ScheduleWrapper = forwardRef<
  ScheduleWrapperRef,
  { modeOfTraining?: string }
>(({ modeOfTraining }, ref) => {
  const pathname = usePathname();
  const wrapperDivRef = useRef<HTMLDivElement>(null);

  const location = hasCookie("location") ? getCookie("location") : "";

  const { course, country, city } = useParams<{
    course: string;
    country?: string;
    city?: string;
  }>();
  const commonVariables = {
    slug: course,
    country: country ? country : location ? location : "us",
    city: city,
    perPage: 5,
    page: 1,
    modeOfTraining,
  };
  const { data: events, refetch } = useSuspenseQuery(GET_EVENTS, {
    variables: {
      ...commonVariables,
    },
  });

  useImperativeHandle(ref, () => ({
    refetch: (variables) => refetch(variables),
    wrapperDivRef: wrapperDivRef,
  }));

  const handlePagination = (value: number) => {
    refetch({
      ...commonVariables,
      page: value,
    });
  };
  const scheduleData = events?.events?.data;

  const paginationData = events?.events?.pageInfo;

  const isSchedule = events?.events?.data?.length > 0;

  const eventSchema = useMemo(() => {
    const result = scheduleData.map((item) => {
      return {
        "@context": "https://schema.org",
        "@type": "EducationEvent",
        name: item.eventName || item.serviceName,

        startDate: item.startDate,
        endDate: item.endDate,
        eventStatus: "https://schema.org/EventScheduled",
        location: {
          "@type": "Place",
          name: "Spoclearn",
          address: {
            "@type": "PostalAddress",
            streetAddress:
              "3500 S Dupont Hwy Suite DK 101, Dover, DE 19901, United States",
            addressLocality: "Dover",
            postalCode: "19901",
            addressRegion: "PA",
            addressCountry: "US",
          },
        },
        image: [process.env.NEXT_PUBLIC_APP_URL + "/images/logo.svg"],
        description: item.eventDescription || item.serviceEventTitle,
        offers: {
          "@type": "AggregateOffer",
          lowPrice: item.eventDetails.sellingPriceNumber,
          highPrice: item.eventDetails.priceNumber,
          url: process.env.NEXT_PUBLIC_APP_URL + pathname,
          availability: "https://schema.org/InStock",
          validFrom: item.startDate,
          price: item.eventDetails.priceNumber,
          priceCurrency: item.eventDetails.currency,
        },
        performer: {
          "@type": "PerformingGroup",
          name: "Spoclearn",
        },
        organizer: {
          "@type": "Organization",
          name: "Spoclearn",
          url: process.env.NEXT_PUBLIC_APP_URL,
        },
      };
    });
    return result;
  }, [scheduleData, pathname]);
  return (
    <Box>
      {/* Schedules loop */}
      {isSchedule && (
        <Box
          className="pt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-4"
          ref={wrapperDivRef}
        >
          {scheduleData?.map((event) => (
            <ScheduleCard key={event.id} data={event} />
          ))}
        </Box>
      )}
      {paginationData && (
        <Pagination
          itemsPerPage={paginationData.perPage}
          count={paginationData.total}
          page={paginationData.currentPage}
          onPageChange={handlePagination}
        />
      )}

      {!isSchedule && <NoScheduleScreen />}
      {eventSchema.length > 0 &&
        eventSchema.map((schema, index) => (
          <StructuredData key={"schedule-schema" + index} data={schema} />
        ))}
    </Box>
  );
});

ScheduleWrapper.displayName = "ScheduleWrapper";

export default ScheduleWrapper;
